import { useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { ChartContainer } from '../../../../../common/chart/chartContainer'
import Area from '../../../../../common/chart/chartContainer/Area'
import Bar from '../../../../../common/chart/chartContainer/Bar'
import { getYAxisNumberProps } from '../../../../../common/chart/chartContainer/helper'
import { UseTypeFontSize } from '../../../../../common/hooks/useTypeFontSize'
import { getUnique } from '../../../../../helpers/common'
import {
  getNoDataComponent,
  getStyleHeightContainer,
  handleIconDownload,
} from '../../helper'
import classes from '../../index.module.scss'
import { Legend } from './../../../../../common/chart/legend/index'
import { formatVal } from './../../../../../helpers/value'
import { TREND_DATA } from './constant'
import { selectDataViewByTrend, selectIndicatorById } from './store/slice'

const Trend = ({ isDynamicReport, isDownloadComponent, id, check = [] }) => {
  const chartId = useRef(getUnique())
  const typeFontSize = UseTypeFontSize()

  const locale = useSelector((state) => state.i18n.locale)
  const dataByTrend = useSelector(selectDataViewByTrend)
  const indicatorById = useSelector(selectIndicatorById)

  const trendData = TREND_DATA.filter(
    ({ id }) => !isDynamicReport || check.includes(id),
  )

  const dataChart = useMemo(() => {
    const result = []
    trendData.forEach((item, index) => {
      result.push(
        ...dataByTrend.map((e) => ({
          year: e.year,
          value: e[item.id],
          name: indicatorById[item.id]?.ratioName,
          industryValue: e[item.industryId],
          industryName: indicatorById[item.industryId]?.ratioName,
        })),
      )
      if (index !== trendData.length - 1) {
        result.push({})
      }
    })
    return result
  }, [dataByTrend])

  const propYAxis = {
    yAxisId: 'left',
    dataKeys: ['value', 'industryValue'],
    isBarChart: true,
  }

  const { width: widthLabel } = getYAxisNumberProps(
    dataChart,
    propYAxis,
    typeFontSize,
  )

  const isNoData = !dataByTrend?.length

  useEffect(() => {
    handleIconDownload(id, isNoData)
  }, [isNoData])

  if (isNoData) {
    return getNoDataComponent(isDynamicReport)
  }

  return (
    <div
      style={{
        height: isDownloadComponent
          ? '100%'
          : getStyleHeightContainer(isDynamicReport, isNoData, 332),
        width: `calc(100% * ${trendData.length / TREND_DATA.length})`,
      }}
      className={`flexColumn ${classes.liquidityContainer}`}
    >
      <div
        style={{ paddingLeft: widthLabel + 'px' }}
        className={`fw600 pb2 dFlex ${classes.titleContainer}`}
      >
        {trendData.map((item, index) => (
          <div key={index} className="w100 textCenter">
            {indicatorById[item.id]?.ratioName}
          </div>
        ))}
      </div>
      <div className="flex1 minH0">
        <ChartContainer
          chartId={chartId.current}
          data={dataChart}
          margin={{
            top: 10,
            left: 0,
            bottom: 0,
            right: 0,
          }}
          width="100%"
          height="100%"
          xAxis={[
            {
              dataKey: 'year',
              tickFormatter: (val) =>
                val ? "'" + val?.toString().slice(-2) : '',
            },
          ]}
          yAxis={[propYAxis]}
          tooltip={{
            schema: {
              content: [
                {
                  dataKey: 'value',
                  label: '',
                  formatLabel: (_, props) => {
                    return props?.payload?.[0]?.payload?.name
                  },
                  formatValue: (value) => formatVal(value, locale),
                },
                {
                  dataKey: 'industryValue',
                  label: '',
                  formatLabel: (_, props) => {
                    return props?.payload?.[0]?.payload?.industryName
                  },
                  formatValue: (value) => formatVal(value, locale),
                },
              ],
            },
          }}
        >
          <Area
            yAxisId={'left'}
            dataKey={'industryValue'}
            fill="#FFEFCB"
            dot={{ fill: '#FFCD60' }}
          />
          <Bar
            yAxisId="left"
            dataKey="value"
            fill="#006AA6"
            maxBarSize={20}
            showLabelEmpty={({ index, barProps }) =>
              !!barProps.data[index].payload?.year
            }
          />
        </ChartContainer>
      </div>

      <Legend
        chartId={chartId.current}
        items={[
          {
            title: 'financialAnalysis.COMPANY',
            dataKey: 'value',
            color: '#006AA6',
          },
          {
            title: 'financialAnalysis.INDUSTRY_AVG',
            dataKey: 'industryValue',
            color: '#FFEFCB',
          },
        ]}
        className="justifyContentCenter gap4 pt2"
      />
    </div>
  )
}

export default Trend
